// 「施工管理」配下のタブ「材料追跡」(モック準拠)。工事を選び、材料ごとの進捗を 2 サブタブで表示する。
//  ① 準備数量進捗: 分母 = この工事に搬入済みの実 QR 数。進捗 = 消費済 / 搬入済。
//  ② 計画数量進捗: 分母 = 主材料計画数量(ConstructionPlanAreaPlannedMaterial.planned_qty を工事×材料で積算)。
//     消費 = 消費済 QR × 主細目 content_quantity。計画超過は 100% クランプ + ⚠️。
// 工事への QR 紐付けは 搬入(MaterialQr.carry_in_id → CarryIn.work_id)で確定。
import { useMemo, useState } from "react";
import {
  Box, Stack, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
  Alert, Tooltip, LinearProgress, Tabs, Tab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import { WorkSelectField } from "../../../components/input/WorkSelectField";
import { useFullDb } from "../../../api/dbSlice";
import { useSelectedWork, resolveWorkId } from "../../../util/useSelectedWork";
import { materialSearchUrl } from "../../../util/Materialperser";
import { MasterLink } from "../../../components/links/EntityLinks";
import { progressColor } from "../../../util/areaStatus";
import type { ExtendedDB } from "../../../types/extendedDB";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import { EmptyState } from "../../../components/EmptyState";
import { FULL_EPS } from "../../../util/consumption";
import { ContentLoading } from "../../../components/Loading";

const CONSUMED_COLOR = "#1565c0";
const REMAIN_COLOR = "#9e9e9e";

type SubTab = "prep" | "plan";

type MaterialAgg = {
  material_id: number;
  material_name: string;
  usage_category: string;
  manufacturer: string;
  carriedIn: number;
  consumedQr: number;
  consumedQrIds: number[];
  onHandQrIds: number[];
  plannedKg: number;
  consumedKg: number;
  unit: string;
};

function isActive(row: { deleted_at?: number | null } | undefined): boolean {
  return row == null || row.deleted_at == null;
}

function fmtQty(n: number): string {
  return (Math.round(n * 100) / 100).toString();
}

// QRごとの累計消費割合(0..1 clamp)。alive な ConstructionMaterialConsumption 行(deleted_at null)の Σ used_ratio。
function buildRatioByQrId(cmc: { material_qr_id: number; used_ratio: number; deleted_at: number | null }[]): Map<number, number> {
  const totals = new Map<number, number>();
  for (const row of cmc) {
    if (row.deleted_at != null) {
      continue;
    }
    totals.set(row.material_qr_id, (totals.get(row.material_qr_id) ?? 0) + row.used_ratio);
  }
  const clamped = new Map<number, number>();
  for (const [qrId, total] of totals) {
    clamped.set(qrId, Math.min(1, Math.max(0, total)));
  }
  return clamped;
}

export function buildMaterialAggs(
  db: Pick<ExtendedDB, "lots" | "carryIns" | "materialQrs" | "materials" | "materialDetails" | "constructionPlanAreas" | "constructionPlanAreaPlannedMaterials" | "constructionMaterialConsumptions">,
  workId: number | "",
): MaterialAgg[] {
  if (workId === "") {
    return [];
  }
  const activeMaterials = db.materials.filter(isActive);
  const activeMaterialIds = new Set(activeMaterials.map((m) => m.id));
  const lotById = new Map(db.lots.filter(isActive).map((l) => [l.id, l]));
  const carryInById = new Map(db.carryIns.filter(isActive).map((c) => [c.id, c]));
  const ratioByQrId = buildRatioByQrId(db.constructionMaterialConsumptions);

  // 主細目(sort_order 最小, 未削除)の 1 セット内容量と単位を材料ごとに用意。
  const kgPerSetByMat = new Map<number, number>();
  const unitByMat = new Map<number, string>();
  for (const mat of activeMaterials) {
    const main = db.materialDetails
      .filter((d) => d.material_id === mat.id && d.deleted_at == null)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
    kgPerSetByMat.set(mat.id, main?.content_quantity ?? 0);
    unitByMat.set(mat.id, main?.content_unit ?? "kg");
  }

  type Acc = { carriedIn: number; consumedRatioSum: number; consumedQrIds: number[]; onHandQrIds: number[]; plannedKg: number };
  const accByMat = new Map<number, Acc>();
  const getAcc = (mid: number): Acc => {
    let a = accByMat.get(mid);
    if (a === undefined) {
      a = { carriedIn: 0, consumedRatioSum: 0, consumedQrIds: [], onHandQrIds: [], plannedKg: 0 };
      accByMat.set(mid, a);
    }
    return a;
  };

  // 準備数量: この工事に搬入済みの QR を材料別に集計。消費量は ConstructionMaterialConsumption の
  // Σ used_ratio から都度計算する(旧データは移行 migration で消費行へ変換済み)。
  for (const q of db.materialQrs) {
    if (q.lot_id == null) {
      continue;
    }
    const lot = lotById.get(q.lot_id);
    if (lot === undefined || lot.material_id == null || !activeMaterialIds.has(lot.material_id)) {
      continue;
    }
    const ci = q.carry_in_id == null ? undefined : carryInById.get(q.carry_in_id);
    if (ci === undefined || ci.work_id !== workId) {
      continue;
    }
    const acc = getAcc(lot.material_id);
    acc.carriedIn += 1;
    const ratio = ratioByQrId.get(q.id) ?? 0;
    acc.consumedRatioSum += ratio;
    if (ratio > 0) {
      acc.consumedQrIds.push(q.id);
    }
    if (1 - ratio > FULL_EPS) {
      acc.onHandQrIds.push(q.id);
    }
  }

  // 計画数量(kg): この工事の施工箇所の計画材料(material_id 単位)を積算。
  const areaIdsOfWork = new Set(db.constructionPlanAreas.filter((a) => a.deleted_at == null && a.work_id === workId).map((a) => a.id));
  for (const pm of db.constructionPlanAreaPlannedMaterials) {
    if (pm.deleted_at != null || !areaIdsOfWork.has(pm.construction_plan_area_id) || !activeMaterialIds.has(pm.material_id)) {
      continue;
    }
    getAcc(pm.material_id).plannedKg += pm.planned_qty;
  }

  const list: MaterialAgg[] = [];
  for (const mat2 of activeMaterials) {
    const acc2 = accByMat.get(mat2.id);
    if (acc2 === undefined || (acc2.carriedIn === 0 && acc2.plannedKg === 0 && acc2.consumedRatioSum === 0)) {
      continue;
    }
    const kgPerSet = kgPerSetByMat.get(mat2.id) ?? 0;
    list.push({
      material_id: mat2.id,
      material_name: mat2.name,
      usage_category: mat2.usage_category ?? "",
      manufacturer: mat2.manufacturer ?? "",
      carriedIn: acc2.carriedIn,
      consumedQr: acc2.consumedRatioSum,
      consumedQrIds: acc2.consumedQrIds,
      onHandQrIds: acc2.onHandQrIds,
      plannedKg: acc2.plannedKg,
      consumedKg: acc2.consumedRatioSum * kgPerSet,
      unit: unitByMat.get(mat2.id) ?? "kg",
    });
  }
  return list.sort((a, b) => a.material_id - b.material_id);
}

export default function MaterialTrackingPage({ pageContext }: { pageContext: PageContext }) {
  const { db, loading } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const navigate = useNavigate();
  const activeWorks = db.works.filter((w) => w.deleted_at == null);
  const [persistedWorkId, setPersistedWorkId] = useSelectedWork();
  const workId = resolveWorkId(persistedWorkId, activeWorks);

  const [subTab, setSubTab] = useState<SubTab>("prep");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const aggs = useMemo<MaterialAgg[]>(() => {
    return buildMaterialAggs(db, workId);
  }, [workId, db.lots, db.carryIns, db.materialQrs, db.materials, db.materialDetails, db.constructionPlanAreas, db.constructionPlanAreaPlannedMaterials, db.constructionMaterialConsumptions]);

  if (loading) {
    return <ContentLoading />;
  }

  const goSearch = (ids: number[]) => {
    if (ids.length === 0) {
      return;
    }
    navigate(materialSearchUrl(ids));
  };

  const rows = subTab === "prep"
    ? aggs.filter((m) => m.carriedIn > 0)
    : aggs.filter((m) => m.plannedKg > 0 || m.consumedKg > 0);

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight="bold">材料追跡</Typography>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <WorkSelectField options={activeWorks.map((w) => ({ id: w.id, label: w.short_name || w.official_name }))} value={workId} onChange={setPersistedWorkId} />
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.materialTracking.tabInfo)}</Alert>

      <Paper variant="outlined">
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab value="prep" label="準備数量進捗" />
          <Tab value="plan" label="計画数量進捗" />
        </Tabs>
        <Box p={2}>
          {workId === "" && <EmptyState message="工事を選択してください" py={4} />}
          {workId !== "" && rows.length === 0 && (
            <EmptyState message={subTab === "prep" ? "この工事に搬入済みの材料がありません" : "この工事に計画／消費のある材料がありません"} py={4} />
          )}
          {workId !== "" && rows.map((m) => {
            const expanded = expandedId === m.material_id;
            const done = subTab === "prep" ? m.consumedQr : m.consumedKg;
            const total = subTab === "prep" ? m.carriedIn : m.plannedKg;
            const unit = subTab === "prep" ? "" : m.unit;
            return (
              <Accordion key={m.material_id} disableGutters expanded={expanded} onChange={() => setExpandedId(expanded ? null : m.material_id)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" gap={1.5} flex={1} mr={2}>
                    <MasterLink type="material" id={m.material_id}
                      name={<Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }}>{m.material_name}</Typography>}
                      onClick={(e) => e.stopPropagation()} />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>{m.usage_category || "-"}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.manufacturer || "-"}</Typography>
                    <Box flex={1} />
                    <ProgressBar done={done} total={total} unit={unit} noun="消費" />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "grey.50" }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {subTab === "prep" ? <PrepDetail m={m} onSearch={goSearch} /> : <PlanDetail m={m} onSearch={goSearch} />}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Paper>
    </Stack>
  );
}

function ProgressBar({ done, total, unit, noun }: { done: number; total: number; unit?: string; noun: string }) {
  const u = unit ? ` ${unit}` : "";
  if (total <= 0 && done <= 0) {
    return <Typography variant="caption" color="text.secondary">-</Typography>;
  }
  const over = done > total;
  const realPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const pct = over ? 100 : Math.min(100, realPct);
  const color = over ? "warning" : progressColor(done, total);
  const tip = over
    ? `${noun} ${fmtQty(done)}${u} / 計画 ${fmtQty(total)}${u} — 計画超過(バーは 100% 表示)`
    : `${noun} ${fmtQty(done)}${u} / 全 ${fmtQty(total)}${u} (${realPct}%)`;
  return (
    <Tooltip title={tip}>
      <Stack gap={0.3} sx={{ minWidth: 180 }}>
        <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 8, borderRadius: 1, bgcolor: "rgba(0,0,0,0.06)" }} />
        <Stack direction="row" alignItems="center" gap={0.5}>
          {over && <WarningAmberIcon sx={{ fontSize: 14, color: "warning.main" }} />}
          <Typography variant="caption" sx={{ color: `${color}.dark`, fontWeight: 600, lineHeight: 1.2 }}>
            {fmtQty(done)}{u} / {fmtQty(total)}{u} {noun} ({pct}%{over ? " 超過" : ""})
          </Typography>
        </Stack>
      </Stack>
    </Tooltip>
  );
}

function PrepDetail({ m, onSearch }: { m: MaterialAgg; onSearch: (ids: number[]) => void }) {
  const onHand = m.carriedIn - m.consumedQr;
  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="baseline" gap={3} flexWrap="wrap">
        <Stat label="搬入済(計)" value={String(m.carriedIn)} />
        <Stat label="消費済" value={fmtQty(m.consumedQr)} />
        <Stat label="搬入済(残)" value={fmtQty(onHand)} />
      </Stack>
      <TwoSegBar doneCount={m.consumedQr} restCount={onHand} doneLabel="消費済" restLabel="搬入済"
        onDone={() => onSearch(m.consumedQrIds)} onRest={() => onSearch(m.onHandQrIds)} />
      <SegLegend doneLabel="消費済" restLabel="搬入済" />
    </Stack>
  );
}

function PlanDetail({ m, onSearch }: { m: MaterialAgg; onSearch: (ids: number[]) => void }) {
  const over = m.consumedKg > m.plannedKg;
  const remain = Math.max(0, m.plannedKg - m.consumedKg);
  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="baseline" gap={3} flexWrap="wrap">
        <Stat label="計画数量" value={`${fmtQty(m.plannedKg)} ${m.unit}`} />
        <Stat label="消費" value={`${fmtQty(m.consumedKg)} ${m.unit}`} warn={over} />
        <Stat label="未施工" value={`${fmtQty(remain)} ${m.unit}`} />
      </Stack>
      {over && (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <WarningAmberIcon sx={{ fontSize: 16, color: "warning.main" }} />
          <Typography variant="caption" color="warning.dark">消費が計画数量を超過しています(計画未入力、または実消費が計画を超過)。</Typography>
        </Stack>
      )}
      <TwoSegBar doneCount={m.consumedKg} restCount={remain} doneLabel="消費済" restLabel="未施工" unit={m.unit} onDone={() => onSearch(m.consumedQrIds)} />
      <SegLegend doneLabel="消費済" restLabel="未施工" />
    </Stack>
  );
}

function TwoSegBar({ doneCount, restCount, doneLabel, restLabel, unit, onDone, onRest, height = 20 }: {
  doneCount: number; restCount: number; doneLabel: string; restLabel: string;
  unit?: string; onDone?: () => void; onRest?: () => void; height?: number;
}) {
  const u = unit ? ` ${unit}` : "";
  const total = doneCount + restCount;
  if (total <= 0) {
    return (
      <Box sx={{ height, bgcolor: "grey.200", borderRadius: 0.5, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" color="text.secondary">対象なし</Typography>
      </Box>
    );
  }
  const seg = (label: string, count: number, color: string, onClick?: () => void) => {
    if (count <= 0) {
      return null;
    }
    const ratio = count / total;
    return (
      <Tooltip title={`${label}: ${fmtQty(count)}${u} (${Math.round(ratio * 100)}%)${onClick ? " — クリックで材料検索" : ""}`}>
        <Box onClick={onClick} sx={{
          flex: `${ratio} 0 0`, bgcolor: color, cursor: onClick ? "pointer" : "default", color: "white",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: Math.max(10, height - 8), fontWeight: "bold",
          "&:hover": onClick ? { opacity: 0.85 } : undefined, userSelect: "none",
        }}>
          {ratio > 0.1 ? fmtQty(count) : ""}
        </Box>
      </Tooltip>
    );
  };
  return (
    <Stack direction="row" sx={{ height, borderRadius: 0.5, overflow: "hidden", boxShadow: "inset 0 0 0 1px #e0e0e0" }}>
      {seg(doneLabel, doneCount, CONSUMED_COLOR, onDone)}
      {seg(restLabel, restCount, REMAIN_COLOR, onRest)}
    </Stack>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <Stack direction="row" alignItems="baseline" gap={1}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700} color={warn ? "warning.dark" : "text.primary"}>{value}</Typography>
    </Stack>
  );
}

function SegLegend({ doneLabel, restLabel }: { doneLabel: string; restLabel: string }) {
  return (
    <Stack direction="row" gap={2} mt={0.5} flexWrap="wrap">
      <LegendItem color={CONSUMED_COLOR} label={doneLabel} />
      <LegendItem color={REMAIN_COLOR} label={restLabel} />
    </Stack>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 0.5 }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}
